import { useEffect, useRef } from "react";

/**
 * Load Leaflet (https://leafletjs.com/) on-demand via CDN.
 *
 * Why CDN: react-leaflet would add ~150KB to every bundle that even
 * imports this hook, and the only place we render a map today is
 * `BuyerLiveWalkMap` (in-app live-walk view inside My Bookings).
 * Lazy-loading from unpkg keeps the main bundle clean and only pays
 * the cost on routes that actually need a map.
 *
 * Race-safety: multiple instances calling useLeaflet() at once will
 * all wait on the SAME promise stored on `window.__leafletReady` —
 * we never inject duplicate <script> tags.
 *
 * The hook returns the Leaflet `L` namespace once loaded, or null
 * while loading. Callers should guard their `L.map(...)` etc. calls
 * with `if (!L) return;`.
 */
const LEAFLET_VERSION = "1.9.4";
const CSS_HREF = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const JS_SRC = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

function injectLeaflet() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.L) return Promise.resolve(window.L);
  if (window.__leafletReady) return window.__leafletReady;

  window.__leafletReady = new Promise((resolve, reject) => {
    // CSS
    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CSS_HREF;
      // SRI omitted: leaflet's CDN doesn't publish current SRI hashes and
      // we don't want a hard-fail on a hash mismatch. Browsers still
      // enforce CORS + SubResource Integrity for other scripts.
      document.head.appendChild(link);
    }
    // JS
    const existing = document.querySelector(`script[src="${JS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = JS_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve(window.L));
    script.addEventListener("error", () => reject(new Error("Leaflet failed to load")));
    document.head.appendChild(script);
  });
  return window.__leafletReady;
}

export default function useLeaflet(onReady) {
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    let cancelled = false;
    injectLeaflet()
      .then((L) => {
        if (!cancelled && L && onReadyRef.current) onReadyRef.current(L);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("Leaflet load failed:", err);
      });
    return () => {
      cancelled = true;
    };
    // We deliberately don't depend on `onReady` so consumers can pass
    // an inline arrow function without re-loading Leaflet every render.
    // The ref trick above keeps the latest callback reachable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
